import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Lock, Search, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TOOL_CATALOG, type ToolAccess, type ToolCatalogItem, type ToolCategory } from "@/lib/toolCatalog";

const CATEGORY_FILTERS: ("all" | ToolCategory)[] = ["all", "convert", "pdf", "special"];

type Props = {
  isPremium: boolean;
  quotaExhausted: boolean;
  onOpen: (tool: ToolCatalogItem) => void;
};

const toneClasses = {
  primary: "bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground",
  accent: "bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-foreground",
  gold: "bg-gold/15 text-gold-foreground group-hover:bg-gold",
  muted: "bg-muted text-muted-foreground group-hover:bg-secondary",
};

const badgeKeyFor = (access: ToolAccess) => {
  if (access === "free") return "homeTools.badges.free";
  if (access === "quota") return "homeTools.badges.quota";
  if (access === "mixed") return "homeTools.badges.mixed";
  return "homeTools.badges.pro";
};

export const HomeModuleGrid = ({ isPremium, quotaExhausted, onOpen }: Props) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<"all" | ToolCategory>("all");

  const visibleTools = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return TOOL_CATALOG.filter((tool) => {
      if (category !== "all" && tool.category !== category) return false;
      if (!needle) return true;
      return `${t(tool.titleKey)} ${t(tool.descriptionKey)}`.toLowerCase().includes(needle);
    });
  }, [category, query, t]);

  return (
    <section id="alati" className="scroll-mt-16 py-14 sm:py-20" aria-labelledby="tools-heading">
      <div className="container mx-auto max-w-6xl px-4">
        <div className="mx-auto mb-9 max-w-2xl text-center">
          <Badge variant="outline" className="mb-3 border-primary/25 bg-primary/5 text-primary">
            <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {t("homeTools.eyebrow")}
          </Badge>
          <h2 id="tools-heading" className="font-display text-3xl font-bold text-foreground sm:text-4xl">ŠTA VAM BH KONVER NUDI</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">BH KONVER JE PRVI BH DIGITALNI ALAT ZA KONVERZIJU AUDIO/VIDEO/MS OFFICE DOKUMENATA KOJI VAM UZ SAMU KONVERZIJU NUDI I DODATNE MOGUĆNOSTI</p>
        </div>

        <div className="sticky top-0 z-20 -mx-4 mb-6 border-b border-border bg-background/90 px-4 py-3 backdrop-blur-md">
          <div className="relative mx-auto max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("homeTools.searchPlaceholder")}
              aria-label={t("homeTools.searchLabel")}
              className="h-11 rounded-full bg-card pl-9"
            />
          </div>
          <nav className="mt-3 flex gap-2 overflow-x-auto pb-1 sm:justify-center" aria-label={t("homeTools.searchLabel")}>
            {CATEGORY_FILTERS.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setCategory(id)}
                aria-pressed={category === id}
                className={cn(
                  "shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors",
                  category === id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:border-accent hover:text-primary",
                )}
              >
                {t(`homeTools.categories.${id}`)}
              </button>
            ))}
          </nav>
        </div>

        {visibleTools.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">{t("homeTools.noResults")}</p>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visibleTools.map((tool) => {
            const Icon = tool.icon;
            const locked = !isPremium && (tool.access === "pro" || (tool.access === "quota" && quotaExhausted));
            return (
              <article key={tool.slug} className="group flex min-h-48 flex-col rounded-lg border border-border bg-card p-5 shadow-xs transition duration-200 hover:-translate-y-1 hover:border-primary/35 hover:shadow-md">
                <div className="mb-5 flex items-start justify-between gap-3">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-lg transition-colors ${toneClasses[tool.tone]}`}>
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {tool.beta && <Badge variant="secondary" className="text-[10px]">BETA</Badge>}
                    <Badge variant={tool.access === "pro" ? "default" : "outline"} className="text-[10px]">
                      {locked && <Lock className="mr-1 h-3 w-3" />}{t(badgeKeyFor(tool.access))}
                    </Badge>
                  </div>
                </div>
                <h3 className="font-display text-base font-semibold text-foreground">{t(tool.titleKey)}</h3>
                <p className="mt-1.5 flex-1 text-xs leading-relaxed text-muted-foreground">{t(tool.descriptionKey)}</p>
                <Button variant="ghost" className="mt-4 h-8 justify-start px-0 text-xs text-primary hover:bg-transparent" onClick={() => onOpen(tool)}>
                  {locked ? t("homeTools.unlock") : t("homeTools.open")} <ArrowRight className="ml-1.5 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </Button>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};